import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  BrowserMockFrame,
  buildRegistrationHref,
  PublicLpFooter,
  PublicLpHeader,
  SectionIntro,
} from "../components/public/HypothesisLandingParts";
import {
  ProductCatalogContentUnavailable,
  type ProductCatalogViewState,
} from "../components/public/ProductCatalogContent";
import { useProductCatalog } from "../hooks/useProductCatalog";
import { getProductCatalogPlan } from "../lib/productCatalog";
import { buildPublicLpDestinationHref, buildPublicLpReturnTo } from "../lib/publicLpReturn";
import { PUBLIC_PAGE_METADATA } from "../publicPageMetadata";

const KNOWLEDGE_USE_METADATA = PUBLIC_PAGE_METADATA.knowledgeUse;

const HEADER_NAVIGATION = [
  { href: "#workflow", label: "使い方" },
  { href: "#examples", label: "活用例" },
  { href: "#president-room", label: "AI社長室とは" },
  { href: "#pricing", label: "料金" },
] as const;

const PAINS = [
  ["01", "紙の資料がそのまま残っている", "展示会でもらったパンフレットや手書きメモ。あとで使おうと思って、そのままになっている。"],
  ["02", "PDFやURLがあちこちにある", "PC、メール、ブラウザ。必要な資料がどこにあるか探すところから始まる。"],
  ["03", "AIに毎回同じ説明をする", "相談するたびに、背景や自社の情報をもう一度探して渡す。"],
  ["04", "保存しただけで終わっている", "情報は持っていても、提案や判断、壁打ちに生かしきれていない。"],
] as const;

const EXAMPLES = [
  {
    title: "展示会でもらったパンフレット",
    steps: ["スマホで撮影", "社長メモ", "AI壁打ちの材料に"],
  },
  {
    title: "自社のサービス資料PDF",
    steps: ["ナレッジへ登録", "企業分析で自社情報として参照", "自社情報を踏まえたAI壁打ちに使う"],
  },
  {
    title: "参考にしているWebページ",
    steps: ["URLを登録", "必要な分析・壁打ちの材料として選ぶ"],
  },
] as const;

const PRESIDENT_ROOM_FEATURES = [
  "日々のAI相談",
  "AI壁打ち",
  "企業リサーチ",
  "ナレッジライブラリ",
  "ニュース・レポート",
  "カレンダー予定確認",
] as const;

function HeroKnowledgeVisual() {
  return (
    <div className="rounded-[2rem] border border-[#DEC7B6] bg-white/90 p-4 shadow-[0_26px_60px_rgba(112,72,52,0.18)] sm:p-6">
      <div className="flex items-center gap-2 border-b border-[#EEE1D5] pb-4">
        <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#D9826F]" />
        <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#E5B96C]" />
        <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#82AE92]" />
        <span className="ml-2 text-xs font-black tracking-[0.12em] text-[#8A756B]">KNOWLEDGE WORKFLOW</span>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-[1.2fr_auto_1.15fr_auto_0.9fr] sm:items-center sm:gap-2 lg:gap-3">
        <div className="space-y-2">
          {["紙・パンフレット", "PDF", "Webページ"].map((item) => (
            <div key={item} className="rounded-xl border border-[#E8D8CC] bg-[#FFFDFC] px-2 py-2.5 text-center text-[11px] font-black text-[#66514A] sm:whitespace-nowrap xl:text-xs">{item}</div>
          ))}
        </div>
        <span aria-hidden className="hidden text-xl font-black text-[#C6A58F] sm:block">→</span>
        <div className="rounded-2xl bg-[#5C443A] px-4 py-6 text-center text-white">
          <p className="text-xs font-black tracking-[0.12em] text-[#F0BEA5]">選んで使えるように</p>
          <p className="mt-2 text-base font-black leading-6">
            <span className="block">ナレッジ</span>
            <span className="block">ライブラリ</span>
          </p>
        </div>
        <span aria-hidden className="hidden text-xl font-black text-[#C6A58F] sm:block">→</span>
        <div className="space-y-2">
          {["企業分析", "AI壁打ち"].map((item) => (
            <div key={item} className="rounded-xl bg-[#E8F0EB] px-3 py-3 text-center text-xs font-black text-[#3D6958]">{item}</div>
          ))}
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs font-black text-[#785B4E]">
        <span className="rounded-full bg-[#F4DED0] px-3 py-1.5">集める</span>
        <span aria-hidden>→</span>
        <span className="rounded-full bg-[#E9E2C8] px-3 py-1.5">選ぶ</span>
        <span aria-hidden>→</span>
        <span className="rounded-full bg-[#DCE8E2] px-3 py-1.5">仕事で使う</span>
      </div>
    </div>
  );
}

function CaptureMock() {
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_1.2fr_0.8fr]">
      {[
        ["PDF", "ファイルを登録", "資料.pdf"],
        ["社長メモ", "スマホで紙を撮影", "パンフレット"],
        ["Webページ", "URLを追加", "https://…"],
      ].map(([title, action, sample]) => (
        <div key={title} className="rounded-2xl border border-[#E5D4C8] bg-[#FFFDFC] p-4 sm:p-3 xl:p-4">
          <span className="inline-flex rounded-full bg-[#F3E7DE] px-2.5 py-1 text-[10px] font-black text-[#9A5D42]">{title}</span>
          <p className="mt-3 text-sm font-black leading-6 text-[#4E3830] sm:whitespace-nowrap sm:text-[13px] xl:text-sm">{action}</p>
          <p className="mt-2 break-all rounded-lg bg-[#F5F0EC] px-2.5 py-2 text-[11px] text-[#79655D]">{sample}</p>
        </div>
      ))}
    </div>
  );
}

function LibraryMock() {
  const items = [
    ["PDF", "事業計画.pdf", "登録済み"],
    ["社長メモ", "展示会パンフレット", "撮影から取込"],
    ["Webページ", "業界動向の記事", "URL登録"],
  ] as const;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-black text-[#4E3830]">ナレッジライブラリ</p>
        <span className="rounded-full bg-[#E8F0EB] px-3 py-1 text-[10px] font-black text-[#3D6958]">必要な仕事で選択</span>
      </div>
      <div className="mt-4 space-y-3">
        {items.map(([type, name, note]) => (
          <div key={name} className="flex min-w-0 items-center gap-3 rounded-2xl border border-[#E5D4C8] bg-[#FFFDFC] p-3">
            <span className="shrink-0 rounded-lg bg-[#F3E7DE] px-2 py-1 text-[10px] font-black text-[#8A4F35]">{type}</span>
            <div className="min-w-0">
              <p className="truncate text-sm font-black text-[#4E3830]">{name}</p>
              <p className="mt-1 text-xs text-[#79655D]">{note}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UseKnowledgeMock() {
  return (
    <div>
      <p className="text-center text-xs font-black tracking-[0.12em] text-[#9A5D42]">使うナレッジを選択</p>
      <div className="mt-3 rounded-2xl border-2 border-[#C58B6B] bg-[#FFF8F3] p-4">
        <div className="flex items-start gap-3">
          <span aria-hidden className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#8A4F35] text-xs font-black text-white">✓</span>
          <div>
            <p className="text-sm font-black text-[#4E3830]">自社サービス資料.pdf</p>
            <p className="mt-1 text-xs leading-5 text-[#75625B]">今回の仕事で参照する資料</p>
          </div>
        </div>
      </div>
      <div className="my-3 text-center text-xl font-black text-[#C6A58F]" aria-hidden>↓</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl bg-[#E8F0EB] p-4 text-center">
          <p className="font-black text-[#355849]">企業分析</p>
          <p className="mt-2 text-xs leading-5 text-[#557366]">相手企業と自社情報を見ながら考える</p>
        </div>
        <div className="rounded-2xl bg-[#F3E7DE] p-4 text-center">
          <p className="font-black text-[#7A4C39]">AI壁打ち</p>
          <p className="mt-2 text-xs leading-5 text-[#7B6257]">資料を材料にAIメンバーへ相談</p>
        </div>
      </div>
    </div>
  );
}

function WorkflowStep({ number, title, children, visual, reverse = false }: {
  number: string;
  title: string;
  children: ReactNode;
  visual: ReactNode;
  reverse?: boolean;
}) {
  return (
    <article className="grid items-center gap-8 rounded-[2rem] border border-[#E8D8CC] bg-white/75 p-5 shadow-[0_16px_42px_rgba(102,72,56,0.09)] md:p-8 lg:grid-cols-2 lg:gap-12">
      <div className={reverse ? "lg:order-2" : ""}>
        <p className="text-xs font-black tracking-[0.2em] text-[#B06E4F]">STEP {number}</p>
        <h3 className="mt-3 text-2xl font-black leading-snug text-[#4E3830] md:text-3xl">{title}</h3>
        <div className="mt-5 space-y-4 text-base leading-8 text-[#6B5851]">{children}</div>
      </div>
      <div className={reverse ? "lg:order-1" : ""}>{visual}</div>
    </article>
  );
}

export function KnowledgeUsePageView({
  catalog,
  catalogLoading = false,
  retryCatalog,
}: ProductCatalogViewState) {
  const location = useLocation();
  const registrationHref = buildRegistrationHref(location.search);
  const returnTo = buildPublicLpReturnTo(location.pathname, location.search);
  const freePlan = catalog ? getProductCatalogPlan(catalog, "free") : null;
  const standardPlan = catalog ? getProductCatalogPlan(catalog, "standard") : null;

  useEffect(() => {
    document.title = KNOWLEDGE_USE_METADATA.title;
    document.querySelector<HTMLMetaElement>('meta[name="description"]')
      ?.setAttribute("content", KNOWLEDGE_USE_METADATA.description);
  }, []);

  return (
    <div className="h-dvh overflow-x-hidden overflow-y-auto scroll-smooth bg-[#FDF8F5] text-[#4E3830]">
      <PublicLpHeader registrationHref={registrationHref} navigation={HEADER_NAVIGATION} navigationLabel="ナレッジ活用LP" />
      <main>
        <section className="relative overflow-hidden">
          <div aria-hidden className="absolute -left-24 top-8 h-72 w-72 rounded-full bg-[#F3D8C8]/60 blur-3xl" />
          <div aria-hidden className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[#DEEAE3]/70 blur-3xl" />
          <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-12 md:px-8 md:pb-24 md:pt-20 lg:grid-cols-[1.02fr_0.98fr]">
            <div>
              <p className="inline-flex rounded-full border border-[#DCB9A6] bg-white/70 px-4 py-2 text-xs font-black tracking-[0.12em] text-[#9A563A]">手元の情報を、次の仕事へ</p>
              <h1 className="mt-6 max-w-[13em] text-4xl font-black leading-[1.25] tracking-tight text-[#49332B] md:max-w-none md:text-[2.7rem] lg:text-[2.85rem] xl:text-[3.05rem]">
                <span className="md:block md:whitespace-nowrap">紙も、PDFも、Webも。</span>
                <span className="md:block md:whitespace-nowrap">ためるだけで終わらせず、</span>
                <span className="text-[#A05F3F] md:block md:whitespace-nowrap">AIが仕事で使える情報に。</span>
              </h1>
              <div className="mt-6 space-y-3 text-base leading-8 text-[#68554D] md:text-lg">
                <p>もらったパンフレット。社内にあるPDF。あとで読みたいWebページ。</p>
                <p>残してはいるけれど、必要なときに探せない。AIに相談するたび、同じ資料をまた探して貼り直す。</p>
                <p>WizMateなら、自社の情報を「ナレッジライブラリ」にまとめておけます。</p>
                <p>紙の資料や手書きメモはスマホで撮影して「社長メモ」に。PDFやWebページも登録し、必要なときに企業分析やAI壁打ちの材料として使います。</p>
              </div>
              <p className="mt-6 border-l-4 border-[#C27A58] pl-4 text-sm font-black leading-7 text-[#5A453D]">WizMateは、社長の仕事を支える「AI社長室」です。</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link to={registrationHref} className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-[#8A4F35] px-7 text-base font-black text-white shadow-[0_14px_30px_rgba(138,79,53,0.25)] transition hover:bg-[#74412C] focus:outline-none focus:ring-2 focus:ring-[#C58B6B] focus:ring-offset-2">無料でWizMateを試す</Link>
                <a href="#workflow" className="inline-flex min-h-12 items-center justify-center px-4 text-sm font-black text-[#8A4F35] underline decoration-[#D0A18A] underline-offset-4">使い方を見る ↓</a>
              </div>
            </div>
            <HeroKnowledgeVisual />
          </div>
        </section>

        <section className="border-y border-[#EADBC8] bg-[#F8EFE8]">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro eyebrow="INFORMATION GAP" title="情報はある。でも、仕事で使えていない。" align="center" />
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {PAINS.map(([number, title, body]) => (
                <article key={number} className="rounded-[1.5rem] border border-[#E1CFC1] bg-white p-5 shadow-[0_12px_28px_rgba(102,72,56,0.07)]">
                  <span className="text-xs font-black text-[#BC7A5B]">{number}</span>
                  <h3 className="mt-3 text-lg font-black leading-7 text-[#4E3830]">{title}</h3>
                  <p className="mt-3 text-sm leading-7 text-[#705D55]">{body}</p>
                </article>
              ))}
            </div>
            <p className="mt-9 text-center text-lg font-black text-[#5D4037]">集めることより、必要なときに使えること。</p>
          </div>
        </section>

        <section id="workflow" className="scroll-mt-20">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro eyebrow="3 STEPS" title="手元の情報を、WizMateで仕事の材料に。" align="center" />
            <div className="mt-12 space-y-7">
              <WorkflowStep number="1" title="手元の情報をWizMateへ" visual={<BrowserMockFrame title="ナレッジを追加" caption="PDF、スマホ撮影した紙資料、Webページを登録します。"><CaptureMock /></BrowserMockFrame>}>
                <p>PDFはそのまま登録。</p>
                <p>参考にしたいWebページも追加します。</p>
                <p>紙でもらった資料やパンフレット、手書きメモは、<strong className="text-[#4E3830]">スマホで撮影して「社長メモ」に。</strong></p>
                <p>紙のまま机に置いておく情報も、外出先で撮影してWizMateへ残せます。</p>
              </WorkflowStep>
              <WorkflowStep number="2" title="ナレッジライブラリにまとめる" reverse visual={<BrowserMockFrame title="ナレッジライブラリ" caption="登録した3種類の情報を、あとから選べる状態にします。"><LibraryMock /></BrowserMockFrame>}>
                <p>登録した情報は、WizMateのナレッジライブラリで管理します。</p>
                <p>必要な情報をWizMateの中に置き、<strong className="text-[#4E3830]">あとからAIとの仕事で選んで使える状態にします。</strong></p>
              </WorkflowStep>
              <WorkflowStep number="3" title="次の仕事で使う" visual={<BrowserMockFrame title="ナレッジを選んで使う" caption="必要なナレッジを選び、企業分析やAI壁打ちの材料にします。"><UseKnowledgeMock /></BrowserMockFrame>}>
                <p>必要なナレッジを選び、企業分析やAI壁打ちの材料にします。</p>
                <p>企業リサーチで詳しく調べた結果もナレッジとして残し、次のAI壁打ちに再利用できます。</p>
                <p>相手企業の情報だけでなく、自社の資料も一緒に見ながら考える。</p>
                <p>事業計画や社長メモを材料に、AIメンバーと壁打ちする。</p>
                <p><strong className="text-[#4E3830]">保存した情報を、次の判断や提案で使います。</strong></p>
                <p className="rounded-2xl bg-[#E8F0EB] px-5 py-4 text-lg font-black leading-8 text-[#355849]">しまっておく資料から、考えるための材料へ。</p>
              </WorkflowStep>
            </div>
          </div>
        </section>

        <section id="examples" className="scroll-mt-20 bg-[#F8EFE8]">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro eyebrow="EXAMPLES" title="こんな情報が、そのまま仕事につながる。" align="center" />
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {EXAMPLES.map(({ title, steps }) => (
                <article key={title} className="min-w-0 rounded-[1.6rem] border border-[#E1CFC1] bg-white p-5 shadow-[0_12px_28px_rgba(102,72,56,0.07)] sm:p-6">
                  <h3 className="break-words text-base font-black leading-7 text-[#4E3830] sm:text-[1.05rem]">{title}</h3>
                  <div className="mt-5 space-y-2">
                    {steps.map((step, index) => (
                      <div key={step}>
                        <p className="break-words rounded-xl bg-[#F5EEE8] px-3 py-3 text-center text-[13px] font-black leading-6 text-[#6B5146] sm:px-4 sm:text-sm">{step}</p>
                        {index < steps.length - 1 ? <p aria-hidden className="py-1 text-center font-black text-[#C08A6E]">↓</p> : null}
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section>
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <div className="rounded-[2rem] border border-[#E2CFC0] bg-white px-6 py-9 shadow-[0_15px_36px_rgba(102,72,56,0.08)] md:px-10 md:py-12">
              <SectionIntro eyebrow="TRUST" title="会社の資料をAIに入れても、大丈夫？" contentClassName="max-w-4xl">
                <p>社内資料や事業計画には、社外に出したくない情報もあります。</p>
                <p className="mt-3">WizMateは回答や分析に必要な範囲で外部AIサービスを利用しますが、<strong className="text-[#4E3830]">ユーザーの明示的な同意がない限り、入力した会話や資料を外部AI事業者の汎用モデル学習へ任意提供しません。</strong></p>
                <p className="mt-3">WizMate側でも、明示的な同意なく、会話や資料を独自モデルの学習・再学習に使いません。</p>
                <p className="mt-3"><strong className="text-[#4E3830]">AIに処理させるために送信した情報が、そのまま一般に公開されたり、他のユーザーへ共有されたりするものではありません。</strong></p>
                <p className="mt-3">詳しいデータの取り扱いは、利用規約・プライバシーポリシーで確認できます。</p>
              </SectionIntro>
              <div className="mt-6 flex flex-wrap gap-5 text-sm font-black text-[#8A4F35]">
                <Link to={buildPublicLpDestinationHref("/privacy", returnTo)} className="underline underline-offset-4">プライバシーポリシーを見る</Link>
                <Link to={buildPublicLpDestinationHref("/terms", returnTo)} className="underline underline-offset-4">利用規約を見る</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#5C443A] text-white">
          <div className="mx-auto max-w-5xl px-5 py-16 text-center md:px-8 md:py-24">
            <p className="text-xs font-black tracking-[0.2em] text-[#F0BEA5]">USE IT AGAIN</p>
            <h2 className="mt-3 text-3xl font-black leading-tight md:text-4xl">毎回、同じ資料を探して貼り直さない。</h2>
            <div className="mx-auto mt-6 max-w-3xl space-y-4 text-base leading-8 text-[#F3E7E0]">
              <p>WizMateでは、自社の資料やメモ、Webページをナレッジライブラリにまとめます。</p>
              <p>必要なときに、その中から資料を選んで企業分析やAI壁打ちへ。</p>
              <p className="text-lg font-black text-white">「調べる」「相談する」たびに、一から前提を用意する手間を減らします。</p>
            </div>
          </div>
        </section>

        <section id="president-room" className="scroll-mt-20 bg-[#E8F0EB]">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-[1fr_0.85fr]">
            <SectionIntro eyebrow="AI PRESIDENT'S OFFICE" title="情報をためる場所ではなく、社長の仕事で使う場所へ。">
              <p>ナレッジライブラリは、WizMateの機能の一つ。</p>
              <p className="mt-4">資料を使って相談する。企業を調べる。AIメンバーと壁打ちする。</p>
              <p className="mt-4"><strong className="text-[#355849]">情報を集めるところから、考えて使うところまで。</strong></p>
              <p className="mt-4">それが「AI社長室 WizMate」です。</p>
            </SectionIntro>
            <div className="rounded-[2rem] bg-white p-6 shadow-[0_18px_42px_rgba(56,91,74,0.13)]">
              <p className="text-center text-xs font-black tracking-[0.18em] text-[#4E7C67]">AI社長室 WizMate</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {PRESIDENT_ROOM_FEATURES.map((item) => <div key={item} className="rounded-xl bg-[#F0F6F2] p-3 text-center text-xs font-bold leading-5 text-[#3F5E51]">{item}</div>)}
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="scroll-mt-20 bg-[#F8EFE8]">
          <div className="mx-auto max-w-5xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro
              eyebrow="PRICING"
              title={freePlan ? `まずは${freePlan.displayName}で、ナレッジ活用を試す。` : "ナレッジ活用に合うプランを確認する。"}
              align="center"
            >
              <p>プラン名と料金は、現在のプラン設定をもとに表示しています。</p>
            </SectionIntro>
            {freePlan && standardPlan ? (
            <div className="mx-auto mt-10 grid max-w-4xl gap-5 md:grid-cols-2">
              {[
                { key: "free", plan: freePlan, cta: "無料で始める" },
                { key: "standard", plan: standardPlan, cta: `まず${freePlan.displayName}で試す` },
              ].map(({ key, plan, cta }) => (
                <article key={key} className={`rounded-[1.8rem] border bg-white p-7 shadow-[0_16px_36px_rgba(102,72,56,0.1)] ${key === "standard" ? "border-[#B87352]" : "border-[#E1CFC1]"}`}>
                  <h3 className="text-xl font-black text-[#4E3830]">{plan.displayName}</h3>
                  <p className="mt-2 text-2xl font-black text-[#8A4F35]">{plan.price.displayLabel}</p>
                  <p className="mt-3 text-sm leading-6 text-[#75625B]">{plan.description}</p>
                  <Link to={registrationHref} className={`mt-7 inline-flex min-h-12 w-full items-center justify-center rounded-xl px-5 text-sm font-black transition focus:outline-none focus:ring-2 focus:ring-[#C58B6B] focus:ring-offset-2 ${key === "standard" ? "bg-[#8A4F35] text-white hover:bg-[#74412C]" : "border border-[#B87352] text-[#8A4F35] hover:bg-[#FFF5EF]"}`}>{cta}</Link>
                </article>
              ))}
            </div>
            ) : (
              <div className="mx-auto mt-10 max-w-4xl">
                <ProductCatalogContentUnavailable
                  loading={catalogLoading}
                  retry={retryCatalog}
                  plansHref={buildPublicLpDestinationHref("/plans", returnTo)}
                />
              </div>
            )}
            <p className="mt-6 text-center text-xs leading-6 text-[#837068]">プランの全機能・注意事項は<Link to={buildPublicLpDestinationHref("/plans", returnTo)} className="ml-1 font-black text-[#8A4F35] underline underline-offset-4">料金ページ</Link>で確認できます。</p>
          </div>
        </section>

        <section className="px-5 py-16 md:px-8 md:py-24">
          <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2.2rem] bg-[linear-gradient(135deg,#714B3A_0%,#9A5D42_100%)] px-6 py-12 text-center text-white shadow-[0_24px_54px_rgba(107,67,48,0.25)] md:px-10 md:py-16">
            <div aria-hidden className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
            <h2 className="relative text-3xl font-black leading-tight md:text-4xl">その資料、しまうだけで終わらせず、次の仕事に使いませんか？</h2>
            <div className="relative mx-auto mt-6 max-w-2xl space-y-2 text-base leading-8 text-[#F4E7E0]">
              <p>紙はスマホで撮る。</p>
              <p>PDFやWebページを登録する。</p>
              <p>必要なときに、企業分析やAI壁打ちで使う。</p>
              <p className="pt-2 text-lg font-black text-white">社長の手元にある情報を、WizMateで仕事の材料に。</p>
            </div>
            <Link to={registrationHref} className="relative mt-8 inline-flex min-h-14 items-center justify-center rounded-2xl bg-white px-8 text-base font-black text-[#7A4C39] shadow-xl transition hover:bg-[#FFF6F1] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#8A4F35]">無料でWizMateを試す</Link>
          </div>
        </section>
      </main>
      <PublicLpFooter returnTo={returnTo} />
    </div>
  );
}

export default function KnowledgeUsePage() {
  const { catalog, loading, error, retry } = useProductCatalog();
  return (
    <KnowledgeUsePageView
      catalog={catalog}
      catalogLoading={loading}
      catalogError={error}
      retryCatalog={retry}
    />
  );
}
